import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { CSSProperties, VFC } from "react";
import styled from "styled-components";
import { LegacyImage, Markdown } from "components";
import { PipelineRoutingLinkButton } from "components/Molecules/PipelineRoutingLinkButton";
import { useAnchor, useCoordinates } from "hooks";
import { FullArrow } from "icons";
import { getImageFormat } from "lib/utils";
import { DataTestId } from "types/enums/testId";
import { Cta1Content } from "types/graphql/generated";

interface Props {
  className?: string;
  content: Cta1Content;
}

const Cta1: VFC<Props> = withErrorBoundary(
  ({
    content: {
      title,
      cta1TextBackground: { data: cta1TextBackground },
      buttonLabel,
      buttonLink,
    },
  }) => {
    const imageCoordinates = useCoordinates({
      containerSelector: ".Cta__titleContainer",
      elSelector: ".ctaImage",
    });

    useAnchor("cta1");

    return (
      <StyledCta1
        className="cta1"
        style={
          {
            "--cta-image-x-position": `${imageCoordinates.x}px`,
            "--cta-image-y-position": `${imageCoordinates.y}px`,
            "--cta-image-width": `${imageCoordinates.elWidth * 1.12}px`,
            "--cta-image-height": `${imageCoordinates.elHeight}px`,
          } as CSSProperties
        }
      >
        <div className="Content">
          <div className="Cta__container">
            <div className="Cta__titleContainer">
              <Markdown className="Cta__title Font_48_54_600">{title}</Markdown>
              {cta1TextBackground && (
                <LegacyImage
                  layout="responsive"
                  transform={{ format: getImageFormat(cta1TextBackground.textBackground.ext) }}
                  src={cta1TextBackground.textBackground.src}
                  className="Cta__titleImage"
                  alt={cta1TextBackground.textBackground.alt}
                  width={cta1TextBackground.textBackground.widht}
                  height={cta1TextBackground.textBackground.height}
                />
              )}
            </div>
            <PipelineRoutingLinkButton
              href={buttonLink}
              rightIcon={<FullArrow />}
              className="mt-10 w-fit"
              dataTestId={DataTestId.Cta1Button}
            >
              {buttonLabel}
            </PipelineRoutingLinkButton>
          </div>
        </div>
      </StyledCta1>
    );
  },
  {}
);

const StyledCta1 = styled.section`
  position: relative;
  margin-bottom: -120px;
  z-index: 1;
  margin-top: -64px;
  padding-top: 64px;

  .Cta__container {
    padding: 96px 64px;
    background:
      url("/images/noise.png"),
      radial-gradient(
          59.96% 84.56% at 108.46% 114.67%,
          rgb(126 100 251 / 75%) 14.44%,
          rgb(255 205 133 / 75%) 59.75%,
          rgb(246 243 241 / 0%) 100%
        )
        /* warning: gradient uses a rotation that is not supported by CSS and may not behave as expected */,
      #faf7f4;
    background-blend-mode: overlay, normal, normal;
    background-repeat: no-repeat;
  }

  @media (min-width: 1200px) {
    .Cta__container {
      background-size: 100% 100%;
    }
  }

  .Cta__titleContainer {
    position: relative;
  }

  .Cta__titleImage {
    position: absolute;
    width: var(--cta-image-width);
    height: var(--cta-image-height);
    top: calc(var(--cta-image-y-position) - 4px);
    left: calc(var(--cta-image-x-position) - 12px);
    z-index: 0;
  }

  .Cta__contactsContainer {
    display: flex;
    margin-top: 48px;
    padding-top: 48px;
    border-top: 1px solid ${({ theme }) => theme.colors.fog["30"]};
  }

  .Cta__contactsList {
    display: flex;
    justify-content: space-between;
    margin-left: 80px;
    max-width: 540px;
    width: 100%;
  }

  .Cta__contactsListItem {
    display: flex;

    &:not(:first-child) {
      a {
        text-decoration: underline;
      }
    }
  }

  .Cta__iconContainer {
    width: 24px;
    height: 24px;
    position: relative;
    margin-top: 4px;
  }

  .Cta__contact {
    margin-left: 16px;
  }

  .Cta__contactsDescription {
    margin-top: 4px;
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    .Cta__container {
      padding: 80px 40px;
    }

    .Cta__title {
      font-size: 40px;
      line-height: 48px;
    }

    .Cta__contactTitle {
      display: none;
    }

    .Cta__contactsList {
      margin-left: unset;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    .Cta__container {
      padding: 64px 24px;
    }

    .Cta__title {
      font-size: 32px;
      line-height: 40px;
    }

    .Cta__titleImage {
      display: none;
    }

    .Cta__contactsList {
      flex-direction: column;
    }

    .Cta__contactsListItem:not(:first-child) {
      margin-top: 32px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.sm}px`}) {
    margin-bottom: unset;

    .Content {
      padding: 0;
    }
  }
`;

gql`
  fragment Cta1Content on ComponentCtaCta1 {
    title
    cta1TextBackground {
      data {
        textBackground: attributes {
          src: hash
          alt: alternativeText
          widht: width
          height: height
          ext
        }
      }
    }
    buttonLink
    buttonLabel
  }
`;

export { Cta1 };
export type { Props as Cta1Props };
